/* eslint-disable no-magic-numbers, no-mixed-operators */

interface ITrack {
  id?: number;
  type?: string;
  codec?: string;
}

const toUnsigned = (value: number): number => {
  return value >>> 0;
};

const parseType = (buffer: Uint8Array): string => {
  return [
    String.fromCharCode(buffer[0]),
    String.fromCharCode(buffer[1]),
    String.fromCharCode(buffer[2]),
    String.fromCharCode(buffer[3]),
  ].join("");
};

const toHexString = (value: number): string => {
  return (`00${value.toString(16)}`).slice(-2);
};

const findBox = (data: Uint8Array, path: string[]) => {
  let results: Uint8Array[] = [];
  let subresults: Uint8Array[];
  let i: number;
  let size: number;
  let type: string;
  let end: number;

  for (i = 0; i < data.byteLength;) {
    size = toUnsigned(data[i] << 24 |
      data[i + 1] << 16 |
      data[i + 2] << 8 |
      data[i + 3]);

    type = parseType(data.subarray(i + 4, i + 8));

    end = size > 1 ? i + size : data.byteLength;

    if (type === path[0]) {
      if (path.length === 1) {
        // this is the end of the path and we've found the box we were
        // looking for
        results.push(data.subarray(i + 8, end));
      } else {
        // recursively search for the next box along the path
        subresults = findBox(data.subarray(i + 8, end), path.slice(1));

        if (subresults.length) {
          results = results.concat(subresults);
        }
      }
    }

    i = end;
  }

  // we've finished searching all of data
  return results;
};

const getTracks = function(init: Uint8Array) {
  const traks = findBox(init, ["moov", "trak"]);
  const tracks: ITrack[] = [];

  traks.forEach((trak: Uint8Array) => {
    const track: ITrack = {};
    const tkhd = findBox(trak, ["tkhd"])[0];

    let view: DataView;
    let tkhdVersion: number;

    // id
    if (tkhd) {
      view = new DataView(tkhd.buffer, tkhd.byteOffset, tkhd.byteLength);
      tkhdVersion = view.getUint8(0);

      track.id = tkhdVersion === 0 ? view.getUint32(12) : view.getUint32(20);
    }

    const hdlr = findBox(trak, ["mdia", "hdlr"])[0];

    // type
    if (hdlr) {
      const type = parseType(hdlr.subarray(8, 12));

      if (type === "vide") {
        track.type = "video";
      } else if (type === "soun") {
        track.type = "audio";
      } else {
        track.type = type;
      }
    }

    // codec
    const stsd = findBox(trak, ["mdia", "minf", "stbl", "stsd"])[0];

    if (stsd) {
      const sampleDescriptions = stsd.subarray(8);
      // gives the codec type string
      track.codec = parseType(sampleDescriptions.subarray(4, 8));

      const codecBox = findBox(sampleDescriptions, [track.codec])[0];
      let codecConfig: Uint8Array;
      let codecConfigType: string;

      if (codecBox) {
        // https://tools.ietf.org/html/rfc6381#section-3.3
        if ((/^[asm]vc[1-9]$/i).test(track.codec)) {
          // we don't need anything but the "config" parameter of the
          // avc1 codecBox
          codecConfig = codecBox.subarray(78);
          codecConfigType = parseType(codecConfig.subarray(4, 8));

          if (codecConfigType === "avcC" && codecConfig.length > 11) {
            track.codec += ".";

            // left padded with zeroes for single digit hex
            // profile idc
            track.codec += toHexString(codecConfig[9]);
            // the byte containing the constraint_set flags
            track.codec += toHexString(codecConfig[10]);
            // level idc
            track.codec += toHexString(codecConfig[11]);
          } else {
            // TODO: show a warning that we couldn't parse the codec
            // and are using the default
            track.codec = "avc1.4d400d";
          }
        } else if ((/^mp4[a,v]$/i).test(track.codec)) {
          // we do not need anything but the streamDescriptor of the mp4a codecBox
          codecConfig = codecBox.subarray(28);
          codecConfigType = parseType(codecConfig.subarray(4, 8));

          if (codecConfigType === "esds" && codecConfig.length > 20 && codecConfig[19] !== 0) {
            track.codec += `.${toHexString(codecConfig[19])}`;
            // this value is only a single digit
            track.codec += `.${toHexString((codecConfig[20] >>> 2) & 0x3f).replace(/^0/, "")}`;
          } else {
            // TODO: show a warning that we couldn't parse the codec
            // and are using the default
            track.codec = "mp4a.40.2";
          }
        } else {
          // flac, opus, etc
          track.codec = track.codec.toLowerCase();
        }
      }
    }

    tracks.push(track);
  });

  return tracks;
};

export const getMimeType = (initBuffer: ArrayBuffer): string => {
  const arr = new Uint8Array(initBuffer);
  const tracks = getTracks(arr);

  const videoTrack = tracks.find(track => track.type === "video");
  const audioTrack = tracks.find(track => track.type === "audio");

  const codecs = [];

  if (videoTrack) {
    codecs.push(videoTrack.codec);
  }

  if (audioTrack) {
    codecs.push(audioTrack.codec);
  }

  return `video/mp4; codecs="${codecs.join(",")}"`;
};
